import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { VIEW_NOTICES, USER_MANAGEMENT } from "../../routes";

interface StatsPanelProps {
  totalUsers: number;
  totalNotices: number;
  upcomingEvents: number;
  activeAdverts: number;
  /** Hide the Active Adverts tile for orgs without Ad Center. */
  showActiveAdverts: boolean;
}

const StatsPanel: React.FC<StatsPanelProps> = ({
  totalUsers,
  totalNotices,
  upcomingEvents,
  activeAdverts,
  showActiveAdverts,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const iconTileSize = theme.spacing(6);
  const iconSize = theme.spacing(3);
  const labelFont = theme.typography.pxToRem(14);
  const valueFont = theme.typography.pxToRem(20); // text-3xl

  const cards = [
    {
      title: "All Users",
      value: totalUsers,
      icon: PeopleIcon,
      color: theme.palette.primary.main,
      onClick: () => navigate(USER_MANAGEMENT),
    },
    {
      title: "Total Notices",
      value: totalNotices,
      icon: DescriptionIcon,
      color: theme.palette.info?.main || theme.palette.primary.main,
      onClick: () => navigate(VIEW_NOTICES),
    },
    {
      title: "Upcoming Events",
      value: upcomingEvents,
      icon: EventNoteIcon,
      color: theme.palette.success.main,
      onClick: () => navigate("/calendar"),
    },
    ...(showActiveAdverts
      ? [
          {
            title: "Active Adverts",
            value: activeAdverts,
            icon: ShoppingCartIcon,
            color: theme.palette.colors?.purple || theme.palette.secondary.main,
            onClick: () => navigate("/ad-center"),
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 1, md: 1.5 },
      }}
    >
      {cards.map((c) => {
        const IconComp = c.icon;
        return (
          <Paper
            key={c.title}
            elevation={0}
            onClick={c.onClick}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                boxShadow: 1,
                borderColor: "primary.main",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
              cursor: "pointer",
              p: { xs: 1.5, md: 2 },
              fontFamily: theme.typography.fontFamily,
              minHeight: { xs: 70, md: 80 },
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 1.5 },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: { xs: theme.spacing(5), md: iconTileSize },
                  height: { xs: theme.spacing(5), md: iconTileSize },
                  bgcolor: alpha(c.color, 0.1),
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconComp
                  sx={{
                    width: { xs: theme.spacing(2.5), md: iconSize },
                    height: { xs: theme.spacing(2.5), md: iconSize },
                    color: c.color,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: {
                      xs: theme.typography.pxToRem(12),
                      md: labelFont,
                    },
                    fontWeight: 500,
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[700]
                        : theme.palette.grey[600],
                    mb: 0.25,
                    lineHeight: 1.2,
                  }}
                >
                  {c.title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: {
                        xs: theme.typography.pxToRem(18),
                        md: valueFont,
                      },
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      lineHeight: 1,
                      m: 0,
                    }}
                  >
                    {c.value}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default StatsPanel;
